import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  CartItem,
  createShopifyCart,
  addLineToShopifyCart,
  updateShopifyCartLine,
  removeLineFromShopifyCart,
  storefrontApiRequest,
  CART_QUERY,
} from '@/lib/shopify';
import { normalizeCheckoutUrl } from '@/lib/checkoutUrl';
import { prepareShopifyCheckout } from '@/lib/checkout';
import { detachCartItems, rebuildShopifyCart } from '@/lib/cartRecovery';

export type { CartItem } from '@/lib/shopify';

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  addItem: (item: Omit<CartItem, 'lineId'>) => Promise<boolean>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  prepareCheckout: () => Promise<string | null>;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isSyncing: false,

      addItem: async (item) => {
        const { items, cartId } = get();
        // Match by variant AND attributes — same variant with a different model
        // (cart line attribute) must be treated as a new line.
        const itemAttrKey = JSON.stringify((item.attributes || []).map(a => [a.key, a.value]).sort());
        const existingItem = items.find(i => {
          if (i.variantId !== item.variantId) return false;
          const existingAttrKey = JSON.stringify((i.attributes || []).map(a => [a.key, a.value]).sort());
          return existingAttrKey === itemAttrKey;
        });
        set({ isLoading: true });
        try {
          if (!cartId) {
            if (items.length > 0) {
              const rebuilt = await rebuildShopifyCart(items);
              if (!rebuilt) return false;
              set(rebuilt);
              return get().addItem(item);
            }

            const result = await createShopifyCart({ ...item, lineId: null });
            set({
              cartId: result.cartId,
              checkoutUrl: result.checkoutUrl
                ? normalizeCheckoutUrl(result.checkoutUrl)
                : null,
              items: [{ ...item, lineId: result.lineId }],
            });
            return true;
          }

          if (existingItem) {
            if (!existingItem.lineId) {
              set({
                cartId: null,
                checkoutUrl: null,
                items: detachCartItems(get().items),
              });
              return get().addItem(item);
            }

            const newQuantity = existingItem.quantity + item.quantity;
            const result = await updateShopifyCartLine(cartId, existingItem.lineId, newQuantity);
            if (result.success) {
              set({ items: get().items.map(i => i.lineId === existingItem.lineId ? { ...i, quantity: newQuantity } : i) });
              return true;
            }
            if (result.cartNotFound) {
              set({
                cartId: null,
                checkoutUrl: null,
                items: detachCartItems(get().items),
              });
              return get().addItem(item);
            }
            return false;
          }

          const result = await addLineToShopifyCart(cartId, { ...item, lineId: null });
          if (result.success && result.lineId) {
            set({ items: [...get().items, { ...item, lineId: result.lineId }] });
            return true;
          }
          if (result.cartNotFound) {
            set({
              cartId: null,
              checkoutUrl: null,
              items: detachCartItems(get().items),
            });
            return get().addItem(item);
          }
          return false;
        } catch (error) {
          console.error('Failed to add item:', error);
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) { await get().removeItem(variantId); return; }
        const { items, cartId } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item) return;

        if (!item.lineId || !cartId) {
          set({
            items: detachCartItems(
              items.map(i => i.variantId === variantId ? { ...i, quantity } : i)
            ),
          });
          return;
        }

        set({ isLoading: true });
        try {
          const result = await updateShopifyCartLine(cartId, item.lineId, quantity);
          if (result.success) {
            set({ items: get().items.map(i => i.variantId === variantId ? { ...i, quantity } : i) });
          } else if (result.cartNotFound) {
            set({
              cartId: null,
              checkoutUrl: null,
              items: detachCartItems(
                get().items.map(i => i.variantId === variantId ? { ...i, quantity } : i)
              ),
            });
          }
        } catch (error) { console.error('Failed to update:', error); }
        finally { set({ isLoading: false }); }
      },

      removeItem: async (variantId) => {
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item) return;

        if (!item.lineId || !cartId) {
          const newItems = items.filter(i => i.variantId !== variantId);
          newItems.length === 0
            ? clearCart()
            : set({ items: detachCartItems(newItems) });
          return;
        }

        set({ isLoading: true });
        try {
          const result = await removeLineFromShopifyCart(cartId, item.lineId);
          if (result.success) {
            const newItems = get().items.filter(i => i.variantId !== variantId);
            newItems.length === 0 ? clearCart() : set({ items: newItems });
          } else if (result.cartNotFound) {
            const newItems = get().items.filter(i => i.variantId !== variantId);
            newItems.length === 0
              ? clearCart()
              : set({
                  cartId: null,
                  checkoutUrl: null,
                  items: detachCartItems(newItems),
                });
          }
        } catch (error) { console.error('Failed to remove:', error); }
        finally { set({ isLoading: false }); }
      },

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null }),

      prepareCheckout: async () => {
        const savedItems = get().items;
        if (savedItems.length === 0) return null;

        set({ isLoading: true });
        try {
          let activeCartId = get().cartId;

          if (!activeCartId) {
            const rebuilt = await rebuildShopifyCart(savedItems);
            if (!rebuilt) return null;
            set(rebuilt);
            activeCartId = rebuilt.cartId;
          }

          // Always request the current URL. If a persisted Shopify cart has
          // expired, rebuild it once from the customer's saved line items.
          let checkout = await prepareShopifyCheckout(activeCartId);
          if (!checkout) {
            const rebuilt = await rebuildShopifyCart(get().items);
            if (!rebuilt) return null;
            set(rebuilt);
            activeCartId = rebuilt.cartId;
            checkout = await prepareShopifyCheckout(activeCartId);
          }
          if (!checkout) return null;

          const checkoutUrl = normalizeCheckoutUrl(checkout.checkoutUrl);
          if (!checkoutUrl) return null;

          set({ checkoutUrl });
          return checkoutUrl;
        } catch (error) {
          console.error('Failed to prepare checkout:', error);
          return null;
        } finally {
          set({ isLoading: false });
        }
      },
      syncCart: async () => {
        const { cartId, isSyncing } = get();
        if (!cartId || isSyncing) return;
        set({ isSyncing: true });
        try {
          const data = await storefrontApiRequest(CART_QUERY, { id: cartId });
          if (!data) return;
          const cart = data?.data?.cart;
          if (!cart || cart.totalQuantity === 0) {
            set({
              cartId: null,
              checkoutUrl: null,
              items: detachCartItems(get().items),
            });
            return;
          }
          const checkoutUrl = normalizeCheckoutUrl(cart.checkoutUrl);
          if (checkoutUrl) set({ checkoutUrl });
        } catch (error) { console.error('Failed to sync cart:', error); }
        finally { set({ isSyncing: false }); }
      },
    }),
    {
      name: 'shopify-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items, cartId: state.cartId, checkoutUrl: state.checkoutUrl }),
    }
  )
);
